// bark.js — paper-birch bark detailing for the trunk and branches.
//
// Two complementary effects, both gated on the birch bark color being selected
// (a no-op for every other trunk color): per-vertex lenticel banding for the
// branch tubes (applyBirchBand), and a canvas texture for the lathe trunk/main
// branches whose clustered vertex rings can't draw fine streaks (syncBirchTexture).
// All pure with respect to the seeded geometry stream — the banding is a pure
// function of geometry and the texture uses Math.random (cosmetic, like
// environment.js), so neither perturbs the seeded build or replay determinism.

import * as THREE from 'three';
import { parameters, app } from './core.js';

// Paper-birch bark: a near-white value (not pure white, so it reads as bark, not
// paper) that also flags the trunk/branch colorers to draw the dark lenticel
// streaks below. Matched by exact value against parameters.truncColor.
const BIRCH_BARK = 0xF0EDE5;
const BIRCH_LENTICEL = new THREE.Color(0x2a2a2a);
// Real paper birch darkens and roughens toward the soil — the bright white peels
// away near the base to a sooty charcoal. Lerp target for that base shading.
const BIRCH_BASE = new THREE.Color(0x33302b);

// Dark horizontal lenticel streaks for paper birch. Given a vertex's angle
// around the trunk/branch and its 0..1 position along it, returns 0..1 darkness:
// mostly 0, with thin irregular bands that break into dashes around the
// circumference so they read as birch lenticels rather than solid rings. Pure
// function of geometry (no RNG) so it never perturbs the seeded build stream or
// replay determinism.
function birchStreak(angle, t) {
  // Two incommensurate sines -> irregularly spaced bands up the length.
  const bands = Math.sin(t * 47.0) * 0.6 + Math.sin(t * 22.3 + 1.7) * 0.4;
  // Keep only the peaks, as thin bands; everything below the threshold is bare.
  const band = (bands - 0.55) / 0.45;
  if (band <= 0) return 0;
  // Break each band into dashes around the circumference; phase drifts with
  // height so the dashes don't stack into vertical seams.
  const dash = 0.5 + 0.5 * Math.sin(angle * 3.0 + t * 60.0);
  return Math.min(1, band) * dash;
}

// Darken an already-finalized bark `color` (in place) with birch lenticel
// streaks, but only when the birch bark is selected — a no-op for every other
// trunk color. `lengthAxis` names the vertex axis running along the trunk/branch
// (trunks/main branches lathe along 'y', branch tubes run along 'z'); the other
// two axes give the angle that breaks the bands into dashes.
export function applyBirchBand(color, positions, i, t, lengthAxis) {
  if (parameters.truncColor !== BIRCH_BARK) return;
  const angle = lengthAxis === 'y'
    ? Math.atan2(positions.getZ(i), positions.getX(i))
    : Math.atan2(positions.getY(i), positions.getX(i));
  const d = birchStreak(angle, t);
  if (d > 0) color.lerp(BIRCH_LENTICEL, d * 0.8);
}

// Darken a paper-birch trunk color (in place) toward the sooty base, strongest at
// the soil and gone by ~30% up. A no-op for every other trunk color. `heightRatio`
// is the vertex's 0..1 position up the trunk. Called by the trunk colorers so the
// build and live recolor stay in sync.
export function applyBirchBase(color, heightRatio) {
  if (parameters.truncColor !== BIRCH_BARK) return;
  const t = Math.min(1, Math.max(0, heightRatio / 0.3));
  const darken = (1 - t) * (1 - t); // eased, max at the base
  if (darken > 0) color.lerp(BIRCH_BASE, darken * 0.85);
}

// Paper-birch bark texture. The lathe trunk/main-branch geometry clusters its
// vertex rings toward the top, so per-vertex banding can't draw streaks on the
// wide lower trunk — a texture sampled per fragment can, using the UVs LatheGeometry
// already provides (u around the trunk, v up it). Drawn once, cached, and reused;
// uses Math.random (cosmetic, like environment.js) so it never touches the seeded
// geometry stream. Multiplies against the near-white birch vertex colors, so the
// dark lenticel dashes read through while the bark keeps its shaded variation.
let _birchTexture = null;
function getBirchTexture() {
  if (_birchTexture) return _birchTexture;
  const w = 256, h = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  // Off-white base with a faint warm tint (matches BIRCH_BARK).
  ctx.fillStyle = '#efeadf';
  ctx.fillRect(0, 0, w, h);

  // Sparse faint grey vertical smudges for a papery feel.
  ctx.globalAlpha = 0.05;
  ctx.fillStyle = '#9a948a';
  for (let i = 0; i < 24; i++) {
    const x = Math.random() * w;
    ctx.fillRect(x, 0, 1 + Math.random() * 2, h);
  }
  ctx.globalAlpha = 1;

  // Horizontal lenticel bands: rows of short dark dashes, irregularly spaced up
  // the height, each broken into dashes around the circumference (x). Wrapping
  // dashes across the x seam keeps the bands continuous around the trunk.
  const bands = 22;
  for (let b = 0; b < bands; b++) {
    const y = (b + 0.5) / bands * h + (Math.random() - 0.5) * (h / bands) * 0.5;
    const thickness = 2 + Math.random() * 4;
    const darkness = 0.35 + Math.random() * 0.45;
    ctx.fillStyle = `rgba(30,28,26,${darkness})`;
    let x = Math.random() * 40;
    while (x < w) {
      const dashLen = 6 + Math.random() * 34;
      ctx.fillRect(x, y, dashLen, thickness);
      if (x + dashLen > w) ctx.fillRect(x - w, y, dashLen, thickness); // wrap the seam
      x += dashLen + 6 + Math.random() * 26; // gap between dashes
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;   // seamless around the trunk
  tex.wrapT = THREE.ClampToEdgeWrapping;
  _birchTexture = tex;
  return tex;
}

// Bark surface normal maps (assets/textures/tree/). These add per-fragment relief
// to the low-poly lathe trunk/branch facets without changing geometry, so the flat
// 6-segment trunk reads as textured bark. Loaded once, cached, and shared across
// materials. Normal maps MUST stay in linear space (NoColorSpace) so their RGB is
// read as a tangent-space vector, not sRGB-decoded as color. Paths resolve against
// the HTML document (repo root), like every other asset load.
const _normalLoader = new THREE.TextureLoader();
const _barkNormals = {};
function getBarkNormal(file, repeatU = 2, repeatV = 4) {
  if (_barkNormals[file]) return _barkNormals[file];
  const tex = _normalLoader.load(`assets/textures/tree/${file}`);
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeatU, repeatV); // tile around (u) and up (v); tune to taste
  _barkNormals[file] = tex;
  return tex;
}

// Each bark color (the truncColor <option> values in index.html) gets a fitting
// surface normal: paper birch wears the smooth paper grain, the mossy green a
// mossy bark, and the browns/gray progressively rougher/older grains. The trunk
// and main branches look it up so changing the bark color swaps the relief too;
// the base/root "ancient" bark and sapwood grain are color-independent and stay
// fixed. Falls back to pine for any unmapped color.
const BARK_NORMALS = {
  0x8B4513: 'bark-pine-normal.jpg',       // Brown
  0xA0522D: 'bark-rough-normal.jpg',      // Sienna
  0x654321: 'bark-old-normal.jpg',        // Dark Brown
  0x696969: 'bark-deep-normal.jpg',       // Gray
  0x6B7355: 'bark-moss-normal.jpg',       // Mossy
  0xF0EDE5: 'bark-paper-gray-normal.jpg', // White (paper birch)
};
function barkNormalFile() {
  return BARK_NORMALS[parameters.truncColor] || 'bark-pine-normal.jpg';
}

// Bark normal maps are an opt-in feature (off by default) so we never fetch the
// texture files until the user turns them on in the settings drawer. setBarkTexture
// flips this and re-applies to the live tree; the apply* helpers (called whenever a
// bark material is built) read it so freshly generated trees match the setting.
let _barkTextureEnabled = false;

// The trunk blends TWO bark normals: the everyday pine bark (normalMap) over most
// of the trunk, with the gnarlier "ancient" bark mixed in near the base, climbing
// higher up taller/thicker trees (set per-vertex in tree.js — bigger = older). We
// can't hang two normalMaps on one MeshStandardMaterial, so inject a second sampler
// and mix the two tangent-space normals by the per-vertex vAncientWeight, sharing
// the pine map's UV/tangent frame and normalScale. The whole block sits under
// #ifdef USE_NORMALMAP_TANGENTSPACE, so with the texture toggle off (normalMap null)
// it compiles to nothing and never touches the ancient sampler. We replace the
// include directive (onBeforeCompile sees includes unexpanded) with our own copy of
// the r166 tangent-space chunk, edited only at the mapN line.
function injectTrunkAncient(shader) {
  shader.uniforms.ancientNormalMap = { value: _barkTextureEnabled ? getBarkNormal('bark-ancient-normal.jpg') : null };
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nattribute float ancientWeight;\nvarying float vAncientWeight;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAncientWeight = ancientWeight;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', '#include <common>\nuniform sampler2D ancientNormalMap;\nvarying float vAncientWeight;')
    .replace('#include <normal_fragment_maps>',
`#ifdef USE_NORMALMAP_TANGENTSPACE
  vec3 mapN = mix( texture2D( normalMap, vNormalMapUv ).xyz, texture2D( ancientNormalMap, vNormalMapUv ).xyz, vAncientWeight ) * 2.0 - 1.0;
  mapN.xy *= normalScale;
  normal = normalize( tbn * mapN );
#endif`);
}

// Apply or clear the trunk bark normals (pine + ancient blend, see injectTrunkAncient),
// honoring the current on/off setting. normalScale dials the bump strength (shared by
// both maps). The geometry must already carry an 'ancientWeight' attribute (tree.js
// sets it from height/girth). Only touches the texture loader when enabled, so the
// normal jpgs aren't fetched until the toggle is first switched on.
export function applyTrunkBark(material) {
  if (!material) return;
  material.normalMap = _barkTextureEnabled ? getBarkNormal(barkNormalFile()) : null;
  material.normalScale.set(0.45, 0.45);
  if (material.onBeforeCompile !== injectTrunkAncient) material.onBeforeCompile = injectTrunkAncient;
  material.needsUpdate = true;
}

// On main branches the bark relief looks wrong out near the thin tips, so we fade
// the texture out early along the branch length. Implemented as a per-vertex
// 'barkFade' attribute (set in tree.js from each vertex's length ratio: ~1 at the
// base, easing to 0 by mid-branch) blended in through onBeforeCompile: capture the
// geometric normal before the normal map perturbs it, then mix back toward it by
// (1 - fade), so fade=0 shows no relief regardless of the normal texture's tiling.
// Robust across three versions (it edits the chunk includes, not their innards) and
// harmless when no normalMap is bound (the maps chunk expands to nothing).
function injectBranchFade(shader) {
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nattribute float barkFade;\nvarying float vBarkFade;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvBarkFade = barkFade;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', '#include <common>\nvarying float vBarkFade;')
    .replace('#include <normal_fragment_maps>',
      'vec3 barkGeoNormal = normal;\n#include <normal_fragment_maps>\nnormal = normalize( mix( barkGeoNormal, normal, vBarkFade ) );');
}

// Like applyTrunkBark but for main branches: same pine normal, faded toward the
// tip via injectBranchFade. The geometry must already carry a 'barkFade' attribute
// (tree.js sets it at branch creation). Attaching onBeforeCompile unconditionally
// keeps the fade compiled in so toggling only swaps the normalMap.
export function applyBranchBarkNormal(material) {
  if (!material) return;
  material.normalMap = _barkTextureEnabled ? getBarkNormal(barkNormalFile()) : null;
  material.normalScale.set(0.45, 0.45);
  if (material.onBeforeCompile !== injectBranchFade) material.onBeforeCompile = injectBranchFade;
  material.needsUpdate = true;
}

// Wood-grain normal for the exposed sapwood — the lighter interior layers that show
// under/through the bark. Unlike the branch bark this covers the whole layer (no
// fade); the grain runs up the trunk/branch via the texture's v tiling. Gated on the
// same Bark Texture toggle, so the sapwood detail comes and goes with the bark.
export function applyWoodGrain(material) {
  if (!material) return;
  material.normalMap = _barkTextureEnabled ? getBarkNormal('wood-grain-normal.jpg', 2, 3) : null;
  material.normalScale.set(0.35, 0.35);
  material.needsUpdate = true;
}

// The surface roots (nebari) wear the gnarled ancient bark at full strength — they
// flare from the oldest part of the tree, so no fade. Same ancient normal the trunk
// base blends toward, just used straight here.
export function applyRootBark(material) {
  if (!material) return;
  material.normalMap = _barkTextureEnabled ? getBarkNormal('bark-ancient-normal.jpg') : null;
  material.normalScale.set(0.5, 0.5);
  material.needsUpdate = true;
}

// Re-apply the right bark normal to every textured mesh in the current tree, by its
// userData tag. Used both by the on/off toggle and after a live bark-color change
// (so the trunk/branch relief swaps to the new color's grain — the apply* helpers
// resolve the file via barkNormalFile()). A no-op on the scene graph when no tree
// exists yet (e.g. boot-time syncSceneToggles, which runs before the first tree).
export function refreshBarkNormals() {
  app.tree?.mesh?.traverse((obj) => {
    if (!obj.isMesh) return;
    if (obj.userData.isTrunk) applyTrunkBark(obj.material);
    else if (obj.userData.isRoot) applyRootBark(obj.material);
    else if (obj.userData.isMainBranch) applyBranchBarkNormal(obj.material);
    else if (obj.userData.isSapwood) applyWoodGrain(obj.material);
  });
}

// Settings-drawer toggle: enable/disable bark normal maps. Stores the flag (so the
// next generated tree picks it up via the apply* helpers) then re-applies across the
// live tree.
export function setBarkTexture(enabled) {
  _barkTextureEnabled = !!enabled;
  refreshBarkNormals();
}

// Set or clear the birch bark texture on a bark material to match the current
// trunk color. Called wherever a lathe bark material is built or live-recolored.
export function syncBirchTexture(material) {
  if (!material) return;
  const want = parameters.truncColor === BIRCH_BARK ? getBirchTexture() : null;
  if (material.map !== want) {
    material.map = want;
    material.needsUpdate = true;
  }
}
